// =======================
// src/paginas/perfil/confirmarAlteracaoEmail.js
// =======================

import React, { useContext, useEffect, useRef, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";

import { useLocation, useNavigate } from "react-router-dom";

import { api } from "@api/api";
import { CondicaoContext } from "../../contexto";
import { tamanhosPaginasAdministrativas } from "../../estilos/global";

const obterMensagemErro = (resultado, mensagemPadrao) => {
  const mensagem = resultado?.message ?? resultado?.msg;

  if (Array.isArray(mensagem)) {
    return mensagem.join(" ");
  }

  return mensagem || mensagemPadrao;
};

export default function ConfirmarAlteracaoEmail() {
  const location = useLocation();

  const navigate = useNavigate();

  const { logout, role } = useContext(CondicaoContext);

  const executadoRef = useRef(false);

  const [carregando, setCarregando] = useState(true);

  const [sucesso, setSucesso] = useState("");

  const [erro, setErro] = useState("");

  useEffect(() => {
    /*
     * Evita segunda chamada durante
     * React StrictMode em desenvolvimento.
     */
    if (executadoRef.current) {
      return;
    }

    executadoRef.current = true;

    const confirmar = async () => {
      const parametros = new URLSearchParams(location.search);

      const token = parametros.get("token");

      if (!token) {
        setErro("Link de confirmação inválido.");

        setCarregando(false);

        return;
      }

      try {
        const response = await fetch(api.confirmProfileEmailChange(), {
          method: "POST",

          credentials: "include",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            token,
          }),
        });

        const resultado = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(
            obterMensagemErro(
              resultado,

              "Não foi possível confirmar a alteração do e-mail.",
            ),
          );
        }

        setSucesso(
          resultado?.msg ||
            "E-mail alterado com sucesso. Faça login novamente.",
        );
      } catch (error) {
        setErro(
          error instanceof Error
            ? error.message
            : "Não foi possível confirmar a alteração do e-mail.",
        );
      } finally {
        setCarregando(false);
      }
    };

    confirmar();
  }, [location.search]);

  const irParaLogin = () => {
    logout();

    navigate("/login", {
      replace: true,
    });
  };

  return (
    <Box
      sx={{
        minHeight: "70vh",

        display: "flex",

        alignItems: "center",

        justifyContent: "center",

        px: 2,

        py: 4,

        bgcolor: "#f5f9fd",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",

          maxWidth: 520,

          p: {
            xs: 3,
            sm: 4,
          },

          textAlign: "center",

          border: "1px solid #c9def4",

          borderRadius: 2,

          boxShadow: "0 8px 28px rgba(32, 79, 122, 0.10)",
        }}
      >
        {carregando ? (
          <>
            <CircularProgress size={38} />

            <Typography
              sx={{
                color: "#56657a",

                mt: 2,
              }}
            >
              Confirmando alteração do e-mail...
            </Typography>
          </>
        ) : sucesso ? (
          <>
            <Box
              sx={{
                width: 72,

                height: 72,

                mx: "auto",

                display: "flex",

                alignItems: "center",

                justifyContent: "center",

                borderRadius: "50%",

                bgcolor: "#e8f5e9",
              }}
            >
              <CheckCircleRoundedIcon
                sx={{
                  color: "#2e7d32",

                  fontSize: 44,
                }}
              />
            </Box>

            <Typography
              component="h1"
              sx={{
                color: "#0767d8",

                fontSize: role === "admin" ? tamanhosPaginasAdministrativas.tituloPagina : "1.45rem",

                fontWeight: 700,

                mt: 2,
              }}
            >
              E-mail alterado
            </Typography>

            <Alert
              severity="success"
              sx={{
                mt: 2,

                textAlign: "left",
              }}
            >
              {sucesso}
            </Alert>

            <Button
              type="button"
              variant="contained"
              onClick={irParaLogin}
              sx={{
                mt: 3,

                minWidth: 180,

                textTransform: "none",

                fontWeight: 600,

                boxShadow: "none",

                bgcolor: "#087cf0",

                "&:hover": {
                  bgcolor: "#0767d8",

                  boxShadow: "none",
                },
              }}
            >
              Fazer login novamente
            </Button>
          </>
        ) : (
          <>
            <Box
              sx={{
                width: 72,

                height: 72,

                mx: "auto",

                display: "flex",

                alignItems: "center",

                justifyContent: "center",

                borderRadius: "50%",

                bgcolor: "#edf6ff",
              }}
            >
              <MarkEmailReadRoundedIcon
                sx={{
                  color: "#087cf0",

                  fontSize: 42,
                }}
              />
            </Box>

            <Typography
              component="h1"
              sx={{
                color: "#0767d8",

                fontSize: role === "admin" ? tamanhosPaginasAdministrativas.tituloPagina : "1.45rem",

                fontWeight: 700,

                mt: 2,
              }}
            >
              Confirmação de e-mail
            </Typography>

            <Alert
              severity="error"
              sx={{
                mt: 2,

                textAlign: "left",
              }}
            >
              {erro}
            </Alert>

            <Button
              type="button"
              variant="outlined"
              onClick={irParaLogin}
              sx={{
                mt: 3,

                minWidth: 150,

                textTransform: "none",

                fontWeight: 600,
              }}
            >
              Ir para o login
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}
